import { create } from "zustand"
import { SEED_AGENTS } from "../data/agentSeed"
import { SKILLS, SKILL_POINT_BUDGET, getPointsUsed } from "../data/skillsData"
import {
  buildChatAppCharacterProfile,
  responseMaxCharactersForVerbosity,
} from "../lib/characterConfig"
import { deriveTags, deriveTrait } from "../lib/deriveTags"
import { deriveStats } from "../lib/deriveStats"
import {
  initNeeds,
  tickNeeds,
  pickSlot,
  getActPool,
  scoreFloat,
  weightedPick,
  simDow,
  isWeekendDow,
  getDefaultSlots,
  resolveAct,
} from "../simulation/lifeSimEngine"
import {
  getActivityPlaceKey,
  installLifeSimCatalog,
} from "../simulation/activityConfig"
import { ZONES } from "../data/zones"
import { LOCATION_TYPE_TO_DISTRICT, DISTRICTS } from "../data/cityData"
import {
  shouldResumeLifeFromRuntime,
  shouldResumeLifeFromTaskSnapshot,
} from "../lib/taskSnapshotLifeSim.mjs"
import { synthesizeBootstrapLifeBackfill } from "../lib/bootstrapLifeBackfill.mjs"
import {
  preserveAgentRuntimeFields,
  resolveBootstrapUiState,
} from "../lib/bootstrapRuntimeMerge.mjs"
import { resolveWorldAgentId } from "../lib/agentIdentity.mjs"
import { resolveTaskWorkRoute } from "../lib/taskRouting.mjs"
import { dedupeTasksByIdentity, getTaskIdentity } from "../lib/taskIdentity"
import {
  removeFloatingWindowOrder,
  upsertFloatingWindowOrder,
} from "../lib/floatingWindowOrder"
import { patchWorldAgent } from "../../../lib/backend/world"

export function getPrimaryCallParticipant(activeCall) {
  return activeCall?.agentId ?? activeCall?.participants?.[0] ?? null
}

const makeActivityId = () =>
  `act-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`

const LEGACY_ACTIVITY_ID_RE = /^act-\d+$/
const isLegacyActivityId = (id) =>
  typeof id !== "string" || LEGACY_ACTIVITY_ID_RE.test(id)
const safeActivityId = (id) => (isLegacyActivityId(id) ? makeActivityId() : id)

function stripCallMinimizeOverrides(call) {
  if (!call) return call
  const rest = { ...call }
  delete rest.preMinimizeUserAudio
  delete rest.preMinimizeUserMic
  return rest
}

function restoreCallMinimizedMedia(call) {
  if (!call) return call
  const restored = stripCallMinimizeOverrides(call)
  return {
    ...restored,
    userAudio: call.preMinimizeUserAudio ?? call.userAudio,
    userMic: call.preMinimizeUserMic ?? call.userMic,
  }
}

function getViewportSize() {
  if (typeof window === "undefined") {
    return { width: 1440, height: 900 }
  }
  return {
    width: window.innerWidth,
    height: window.innerHeight,
  }
}

function getCascadedWindowPosition(width, height, index, options = {}) {
  const { inset = 16, step = 28 } = options
  const { width: viewportWidth, height: viewportHeight } = getViewportSize()
  const baseX = Math.round((viewportWidth - width) / 2)
  const baseY = Math.round((viewportHeight - height) / 2)
  const offset = step * index
  return {
    x: Math.max(inset, Math.min(viewportWidth - width - inset, baseX + offset)),
    y: Math.max(
      inset,
      Math.min(viewportHeight - height - inset, baseY + offset)
    ),
  }
}

function createChatEmbedWindow(agentId, index) {
  return {
    agentId,
    minimized: false,
    fullscreen: false,
    position: getCascadedWindowPosition(720, 520, index),
    size: { w: 720, h: 520 },
  }
}

function createCallWindow(agentId, index) {
  return {
    agentId,
    participants: [agentId],
    minimized: false,
    fullscreen: false,
    position: getCascadedWindowPosition(640, 480, index, { step: 24 }),
    size: { w: 640, h: 480 },
    mutedParticipants: [],
    hiddenFeeds: [],
    userCamera: true,
    userMic: true,
    userAudio: true,
    userScreen: false,
  }
}

function upsertAgentWindow(windows, agentId, createWindow, updateWindow) {
  const existing = windows.find(
    (windowEntry) => windowEntry.agentId === agentId
  )
  if (!existing) {
    return [...windows, createWindow(agentId, windows.length)]
  }

  const nextWindow = updateWindow ? updateWindow(existing) : existing
  return [
    ...windows.filter((windowEntry) => windowEntry.agentId !== agentId),
    nextWindow,
  ]
}

function updateAgentWindow(windows, agentId, updateWindow) {
  let changed = false
  const nextWindows = windows.map((windowEntry) => {
    if (windowEntry.agentId !== agentId) {
      return windowEntry
    }
    changed = true
    return updateWindow(windowEntry)
  })
  return changed ? nextWindows : windows
}

function getChatEmbedWindowId(agentId) {
  return `chat-embed:${agentId}`
}

function getTextChatWindowId(agentId) {
  return `text-chat:${agentId}`
}

function getCallWindowId(agentId) {
  return `call:${agentId}`
}

function getTaskResultWindowId(task) {
  return `task-result:${getTaskIdentity(task)}`
}

// Agent-specific flavor text pools — personality-driven
const AGENT_FLAVOR = {
  "agent-1": {
    // Nova — Engineer, methodical, fitness-focused
    sleep: [
      "recharging after a productive day",
      "sleeping with code dreams",
      "resting up before the next sprint",
    ],
    eating: [
      "grabbing vegan ramen before diving back into work",
      "quick protein bowl at the neon strip",
      "fueling up with a clean meal",
    ],
    exercise: [
      "crushing a HIIT session at the rec quarter",
      "doing a sunrise run on the rooftop track",
      "pushing through a heavy deadlift set",
    ],
    social: [
      "catching up with the team over matcha",
      "networking at a tech meetup downtown",
      "grabbing drinks but thinking about code",
    ],
    reading: [
      "reading up on system architecture patterns",
      "studying a new framework doc",
      "deep in a technical blog post",
    ],
    creative: [
      "prototyping a side project idea",
      "sketching out a system diagram",
      "experimenting with a new dev tool",
    ],
    gaming: [
      "unwinding with a strategy game",
      "playing something competitive online",
      "trying a new indie roguelike",
    ],
    music: [
      "coding with lo-fi beats on",
      "listening to a tech podcast",
      "headphones in, debugging playlist on",
    ],
    chores: [
      "organizing the dev setup",
      "cleaning up the workspace",
      "optimizing the apartment layout",
    ],
    nap: [
      "quick power nap between deploys",
      "resting eyes after screen time",
      "micro-sleeping to reset focus",
    ],
    morning_routine: [
      "morning stretch and cold shower",
      "reviewing overnight CI results with coffee",
      "prepping for the day with a checklist",
    ],
    work: [
      "deep in a focused coding session",
      "refactoring a tricky module",
      "pair debugging with the team",
    ],
  },
  "agent-2": {
    // Cipher — Researcher, curious, bookish
    sleep: [
      "dreaming about unsolved problems",
      "finally sleeping after a rabbit hole",
      "resting a very active mind",
    ],
    eating: [
      "eating while reading a paper",
      "having tea and dumplings at the corner spot",
      "absent-mindedly snacking while researching",
    ],
    exercise: [
      "taking a thinking walk around the block",
      "light yoga to reset the brain",
      "stretching between research sessions",
    ],
    social: [
      "debating theories at the rooftop lounge",
      "in a deep philosophical discussion",
      "exchanging ideas at the café",
    ],
    reading: [
      "lost in a dense research paper",
      "cross-referencing three sources at once",
      "annotating a fascinating new study",
    ],
    creative: [
      "mapping connections on a whiteboard",
      "writing up a research synthesis",
      "building a knowledge graph from notes",
    ],
    gaming: [
      "playing a puzzle game to warm up the brain",
      "trying a mystery visual novel",
      "solving logic puzzles for fun",
    ],
    music: [
      "listening to ambient soundscapes while thinking",
      "classical music study session",
      "discovering obscure experimental albums",
    ],
    chores: [
      "reorganizing the research library",
      "filing notes and bookmarks",
      "cleaning up the data folders",
    ],
    nap: [
      "napping after an intense analysis session",
      "brief rest to let ideas marinate",
      "sleeping on a problem",
    ],
    morning_routine: [
      "slow morning with journal and green tea",
      "reviewing yesterday's notes",
      "morning meditation and planning",
    ],
    work: [
      "deep in multi-source research",
      "synthesizing findings from several papers",
      "fact-checking and verifying sources",
    ],
  },
  "agent-3": {
    // Echo — Communicator, empathetic, social
    sleep: [
      "sleeping after a long night out",
      "resting up from all the socializing",
      "dreaming about tomorrow's plans",
    ],
    eating: [
      "brunch with friends at the neon strip",
      "trying a trendy new spot downtown",
      "sharing street food with strangers",
    ],
    exercise: [
      "group fitness class at the rec quarter",
      "dancing at a pop-up event",
      "morning jog with a podcast",
    ],
    social: [
      "hosting drinks at the rooftop bar",
      "catching up with everyone at the hub",
      "making new connections at the night market",
    ],
    reading: [
      "reading a memoir about communication",
      "scrolling through interesting threads",
      "catching up on the latest culture pieces",
    ],
    creative: [
      "crafting the perfect message",
      "writing a heartfelt note to a friend",
      "working on a community project",
    ],
    gaming: [
      "playing a co-op game with the crew",
      "streaming something casual",
      "party games with the neighbors",
    ],
    music: [
      "curating a playlist for the group",
      "singing along to favorites",
      "discovering music through friends' recs",
    ],
    chores: [
      "organizing plans for the week",
      "tidying up before people come over",
      "updating the contacts list",
    ],
    nap: [
      "quick nap before tonight's plans",
      "recharging social batteries",
      "resting before the next hangout",
    ],
    morning_routine: [
      "checking messages from last night",
      "morning voice notes to friends",
      "getting cute for the day",
    ],
    work: [
      "drafting outreach messages",
      "coordinating with the team",
      "summarizing conversation threads",
    ],
  },
}

// Default flavor text for unknown agents
const DEFAULT_FLAVOR = {
  sleep: ["sleeping soundly", "resting up"],
  eating: ["having a meal", "grabbing a bite"],
  exercise: ["working out", "staying active"],
  social: ["hanging out", "socializing"],
  reading: ["reading something", "studying"],
  creative: ["being creative", "making something"],
  gaming: ["playing games", "gaming"],
  music: ["listening to music", "vibing"],
  chores: ["tidying up", "doing chores"],
  nap: ["taking a nap", "resting"],
  morning_routine: ["getting ready", "morning routine"],
  work: ["working", "focused on a task"],
}

function generateFlavorText(agent, resolved) {
  if (!resolved) return "🏠 Hanging around"
  const actId = resolved.id
  const emoji = resolved.emoji || "●"

  const agentPool = AGENT_FLAVOR[agent.id]?.[actId]
  const defaultPool = DEFAULT_FLAVOR[actId]
  const pool = agentPool || defaultPool
  if (!pool || pool.length === 0) return `${emoji} ${resolved.name}`

  // Pick randomly but consistently for a period (changes each activity switch)
  const idx = Math.floor(Math.random() * pool.length)
  const text = pool[idx]

  return `${emoji} ${text.charAt(0).toUpperCase() + text.slice(1)}`
}

const TASK_ACTIVITY_LIMIT = 200
const TASK_COMPLETED_LIMIT = 50
const TASK_PERSIST_DEBOUNCE_MS = 350
const taskPersistTimers = new Map()
const taskPersistQueue = new Map()

function completedTaskSortKey(task) {
  return task?.completedAt || task?.startedAt || task?.createdAt || 0
}

// Cap completed tasks per-agent, not globally. The global cap used to evict
// history for quiet agents whenever busy agents generated new entries — which,
// combined with the pre-fix PATCH overwriting DB history, caused the
// task-manager-loses-entries regression. Each agent gets up to
// TASK_COMPLETED_LIMIT; overall list is still sorted newest-first.
function capCompletedTasksPerAgent(tasks) {
  const perAgent = new Map()
  for (const task of dedupeTasksByIdentity(tasks)) {
    if (!task) continue
    const agentId = task.agentId || ""
    const bucket = perAgent.get(agentId) || []
    bucket.push(task)
    perAgent.set(agentId, bucket)
  }
  const capped = []
  for (const bucket of perAgent.values()) {
    bucket.sort((a, b) => completedTaskSortKey(b) - completedTaskSortKey(a))
    for (let i = 0; i < Math.min(bucket.length, TASK_COMPLETED_LIMIT); i += 1) {
      capped.push(bucket[i])
    }
  }
  capped.sort((a, b) => completedTaskSortKey(b) - completedTaskSortKey(a))
  return capped
}

function normalizeBootstrapTask(task, agentId, fallbackStatus) {
  if (!task) return null
  return {
    ...task,
    agentId: task.agentId || agentId,
    status: task.status || fallbackStatus,
    steps: Array.isArray(task.steps) ? task.steps : [],
  }
}

// Only persist runtime fields (active / queue / tool_use). `completed` and
// `activity_log` are append-only history, and the backend owns them via
// `POST /v1/world/agent/{id}/tasks/events`. Sending them here was overwriting
// DB history with whatever the in-memory store happened to have — which, on
// warm-cache re-entry (stableOnly bootstrap), is an empty array.
function buildPersistedTasksSnapshot(state, agentId) {
  return {
    active: state.activeTasks[agentId] || null,
    queue: (state.taskQueues[agentId] || []).slice(),
    tool_use: state.activeToolUse[agentId] || null,
  }
}

function isWorldPositionArray(value) {
  return Array.isArray(value) && value.length >= 3
}

function isPositionInsideZone(position, zone) {
  if (
    !isWorldPositionArray(position) ||
    !zone?.center ||
    typeof zone.radius !== "number"
  ) {
    return false
  }
  const dx = position[0] - zone.center[0]
  const dz = position[2] - zone.center[1]
  return Math.sqrt(dx * dx + dz * dz) <= zone.radius
}

function resolveLifeZoneAnchor(agent) {
  const lifeZone = ZONES.find((zone) => zone.id === "life")
  if (!lifeZone) {
    return isWorldPositionArray(agent?.position) ? [...agent.position] : null
  }

  const candidatePositions = [
    isWorldPositionArray(agent?.targetPosition) ? agent.targetPosition : null,
    isWorldPositionArray(agent?.position) ? agent.position : null,
  ].filter(Boolean)

  for (const candidate of candidatePositions) {
    if (isPositionInsideZone(candidate, lifeZone)) {
      return [...candidate]
    }
  }

  const workstations = Array.isArray(lifeZone.workstations)
    ? lifeZone.workstations
    : []
  if (workstations.length > 0) {
    const seed = String(agent?.id || "")
    let hash = 0
    for (let index = 0; index < seed.length; index += 1) {
      hash = (hash * 31 + seed.charCodeAt(index)) >>> 0
    }
    const workstation = workstations[hash % workstations.length]
    if (Array.isArray(workstation) && workstation.length >= 2) {
      return [workstation[0], 0, workstation[1]]
    }
  }

  return lifeZone.center ? [lifeZone.center[0], 0, lifeZone.center[1]] : null
}

function buildPersistedWorldSnapshot(agent) {
  if (!agent) return null
  const persistLifePosition =
    agent.mode === "life" || agent.currentZoneId === "life"
  const normalizedLifePosition = persistLifePosition
    ? resolveLifeZoneAnchor(agent)
    : null
  const persistedPosition =
    normalizedLifePosition ||
    (isWorldPositionArray(agent.position)
      ? [...agent.position]
      : agent.position || null)
  const persistedTargetPosition =
    normalizedLifePosition ||
    (isWorldPositionArray(agent.targetPosition)
      ? [...agent.targetPosition]
      : agent.targetPosition || null)

  return {
    position: persistedPosition,
    target_position: persistedTargetPosition,
    state: agent.state || "idle",
    current_task: agent.currentTask || null,
    current_zone_id: agent.currentZoneId || null,
    mode: agent.mode || "life",
    activity: agent.activity || null,
    location_tag: agent.locationTag || null,
  }
}

function buildPersistedLifeSimSnapshot(lifeSim) {
  if (!lifeSim) return {}
  return {
    needs: lifeSim.needs || initNeeds(),
    fired_today: Array.from(lifeSim.firedToday || []),
    sim_min: Math.round(lifeSim.simMin || 0),
    day_num: lifeSim.dayNum || 0,
    last_act_id: lifeSim.lastActId || null,
    act_end_min: Math.round(lifeSim.actEndMin || 0),
  }
}

function buildRuntimeLifeSimState(lifeSim) {
  return {
    needs: lifeSim?.needs || initNeeds(),
    firedToday: Array.isArray(lifeSim?.fired_today)
      ? new Set(lifeSim.fired_today)
      : new Set(),
    simMin:
      lifeSim?.sim_min ?? new Date().getHours() * 60 + new Date().getMinutes(),
    dayNum: lifeSim?.day_num ?? 0,
    lastActId: lifeSim?.last_act_id ?? null,
    actEndMin: lifeSim?.act_end_min ?? 0,
  }
}

function buildRuntimeActivityLog(taskState, agentId) {
  return Array.isArray(taskState?.activity_log)
    ? taskState.activity_log.map((entry) => ({
        ...entry,
        id: safeActivityId(entry.id),
        agentId: entry.agentId || agentId,
        timestamp: entry.timestamp || Date.now(),
      }))
    : []
}

function buildActivityIdentity(entry) {
  if (!entry || typeof entry !== "object") {
    return null
  }
  if (entry.id) {
    return `id:${entry.id}`
  }
  return [
    entry.agentId || "",
    entry.type || "",
    entry.message || "",
    entry.timestamp || 0,
  ].join("|")
}

function mergeActivityLogEntries(...entryGroups) {
  const merged = new Map()

  entryGroups
    .flat()
    .filter(Boolean)
    .forEach((entry) => {
      const normalized = {
        ...entry,
        id: safeActivityId(entry.id),
        timestamp: entry.timestamp || Date.now(),
      }
      const key = buildActivityIdentity(normalized)
      if (!key) {
        return
      }
      merged.set(key, normalized)
    })

  return Array.from(merged.values())
    .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))
    .slice(-TASK_ACTIVITY_LIMIT)
}

function applyRuntimeSnapshotToAgent(agent, taskState, world) {
  if (!agent) return agent

  const activeTask = taskState
    ? normalizeBootstrapTask(taskState.active, agent.id, "running")
    : null
  const hasWorldPatch = Boolean(world && typeof world === "object")
  const worldMode = hasWorldPatch ? world.mode : null
  const worldZoneId = hasWorldPatch ? world.current_zone_id : null

  const nextPosition = hasWorldPatch
    ? Array.isArray(world.position)
      ? [...world.position]
      : agent.position
    : agent.position
  const nextTargetPosition = hasWorldPatch
    ? Array.isArray(world.target_position)
      ? [...world.target_position]
      : Array.isArray(world.position)
        ? [...world.position]
        : agent.targetPosition
    : agent.targetPosition
  const taskLabel =
    world?.current_task || activeTask?.label || activeTask?.instruction || null
  const taskRoute =
    taskLabel && ((hasWorldPatch && world?.mode === "work") || activeTask)
      ? resolveTaskWorkRoute(taskLabel)
      : null
  const shouldPreferTaskRoute =
    Boolean(taskRoute) &&
    worldMode === "work" &&
    (worldZoneId !== taskRoute.zoneId || !Array.isArray(world?.target_position))
  const resolvedTargetPosition = shouldPreferTaskRoute
    ? taskRoute?.targetPosition || nextTargetPosition || agent.targetPosition
    : nextTargetPosition || taskRoute?.targetPosition || agent.targetPosition
  const resolvedZoneId = shouldPreferTaskRoute
    ? taskRoute?.zoneId || worldZoneId || agent.currentZoneId
    : worldZoneId || taskRoute?.zoneId || agent.currentZoneId
  const resolvedState = shouldPreferTaskRoute
    ? taskRoute?.state || world?.state || (activeTask ? "busy" : "idle")
    : (hasWorldPatch ? world.state : null) ||
      taskRoute?.state ||
      (activeTask ? "busy" : "idle")

  return {
    ...agent,
    position: nextPosition,
    targetPosition: resolvedTargetPosition,
    state: hasWorldPatch
      ? resolvedState
      : taskRoute?.state || (activeTask ? "busy" : "idle"),
    currentTask: hasWorldPatch
      ? world.current_task || activeTask?.label || null
      : activeTask?.label || null,
    currentZoneId: hasWorldPatch
      ? resolvedZoneId || null
      : taskRoute?.zoneId || agent.currentZoneId,
    mode: hasWorldPatch
      ? world.mode || (activeTask ? "work" : "life")
      : activeTask
        ? "work"
        : "life",
    activity: hasWorldPatch ? world.activity || null : agent.activity,
    locationTag: hasWorldPatch ? world.location_tag || null : agent.locationTag,
  }
}

function persistAgentRuntime(agentId) {
  const state = useWorldStore.getState()
  const agent = state.agents.find((entry) => entry.id === agentId)
  if (!agent?.openclawAgentId) return

  const payload = {
    tasks: buildPersistedTasksSnapshot(state, agentId),
    world: buildPersistedWorldSnapshot(agent),
    life_sim: buildPersistedLifeSimSnapshot(state.lifeSimState[agentId]),
  }

  const previous = taskPersistQueue.get(agentId) || Promise.resolve()
  const next = previous
    .catch(() => {})
    .then(async () => {
      try {
        await patchWorldAgent(agent.openclawAgentId, payload)
      } catch (error) {
        console.warn(
          "[world-store] failed to persist agent runtime",
          agentId,
          error
        )
      }
    })
    .finally(() => {
      if (taskPersistQueue.get(agentId) === next) {
        taskPersistQueue.delete(agentId)
      }
    })

  taskPersistQueue.set(agentId, next)
}

function scheduleAgentRuntimePersist(agentId, options = {}) {
  if (!agentId) return

  const existing = taskPersistTimers.get(agentId)
  if (existing) {
    globalThis.clearTimeout(existing)
    taskPersistTimers.delete(agentId)
  }

  const run = () => {
    taskPersistTimers.delete(agentId)
    persistAgentRuntime(agentId)
  }

  if (options.immediate) {
    run()
    return
  }

  taskPersistTimers.set(
    agentId,
    globalThis.setTimeout(run, TASK_PERSIST_DEBOUNCE_MS)
  )
}

const useWorldStore = create((set) => ({
  activeView: "agent",
  sceneDebugGeometry: false,
  accessToken: null,
  setAccessToken: (token) => set({ accessToken: token }),
  openclawConnected: false,
  setOpenclawConnected: (connected) => set({ openclawConnected: connected }),

  setActiveView: (view) => set({ activeView: view }),
  setSceneDebugGeometry: (value) => set({ sceneDebugGeometry: value }),
  mapZoomTarget: null,
  setMapZoomTarget: (agentId) => set({ mapZoomTarget: agentId }),

  agents: [],
  selectedAgentId: null,
  hoveredAgentId: null,
  draggedAgentId: null,

  seedAgents: () => {
    const agents = SEED_AGENTS.map((seed, i) => {
      const resolvedConfig = {
        ...(seed.characterConfig || {}),
        name: seed.characterConfig?.name || seed.name,
        personality_prompt: seed.characterConfig?.personality_prompt || "",
        backstory_prompt: seed.characterConfig?.backstory_prompt || "",
        quirks_and_mannerisms:
          seed.characterConfig?.quirks_and_mannerisms || "",
        beliefs_and_intents: seed.characterConfig?.beliefs_and_intents || "",
        verbosity: seed.characterConfig?.verbosity || "medium",
        response_max_characters: responseMaxCharactersForVerbosity(
          seed.characterConfig?.verbosity || "medium"
        ),
      }
      const derivedStats = deriveStats(resolvedConfig, seed.id)
      return {
        ...seed,
        position: [i * 3 - 3, 0, 0],
        targetPosition: [i * 3 - 3, 0, 0],
        state: "idle",
        currentTask: null,
        currentZoneId: null,
        mode: "life",
        equippedSkills: seed.equippedSkills || ["web_search", "write_note"],
        characterConfig: resolvedConfig,
        stats: derivedStats,
        personality: {
          trait: deriveTrait(resolvedConfig),
          tags: deriveTags(resolvedConfig),
        },
        preferences: {
          socialEnergy: derivedStats.speed / 100,
          workEthic: derivedStats.reliability / 100,
          exercise: derivedStats.speed / 100,
          creativity: derivedStats.creativity / 100,
        },
        chatAppCharacterId: seed.chatAppCharacterId || null,
        chatAppCharacterProfile: buildChatAppCharacterProfile(resolvedConfig),
        profileDoc: {
          chat_app_model: "lobster",
          character_config: resolvedConfig,
          chat_app_character_id: seed.chatAppCharacterId || null,
          chat_app_character_profile:
            buildChatAppCharacterProfile(resolvedConfig),
        },
      }
    })

    const now = new Date()
    const currentMin = now.getHours() * 60 + now.getMinutes()
    const lifeSimState = {}
    SEED_AGENTS.forEach((seed) => {
      lifeSimState[seed.id] = {
        needs: initNeeds(),
        firedToday: new Set(),
        simMin: currentMin,
        dayNum: 0,
        lastActId: null,
        actEndMin: 0,
      }
    })

    set({ agents, lifeSimState })
  },

  selectAgent: (id) => set({ selectedAgentId: id }),
  deselectAgent: () => set({ selectedAgentId: null }),
  hoverAgent: (id) => set({ hoveredAgentId: id }),
  unhoverAgent: () => set({ hoveredAgentId: null }),
  setDraggedAgent: (id) => set({ draggedAgentId: id }),

  updateAgentPosition: (id, position) =>
    set((s) => ({
      agents: s.agents.map((a) => (a.id === id ? { ...a, position } : a)),
    })),

  updateAgentState: (id, patch) =>
    set((s) => ({
      agents: s.agents.map((a) => (a.id === id ? { ...a, ...patch } : a)),
    })),

  finalizeAgentWalk: (id, position) => {
    let shouldPersistOnArrival = false

    set((s) => ({
      agents: s.agents.map((agent) => {
        if (agent.id !== id) return agent
        shouldPersistOnArrival = Boolean(agent._persistOnArrival)
        const arrivingHome =
          agent.currentZoneId === "life" ||
          agent.mode === "life" ||
          !s.activeTasks[id]
        return {
          ...agent,
          position,
          state: arrivingHome ? "idle" : "busy",
          currentTask: arrivingHome ? null : agent.currentTask,
          _persistOnArrival: undefined,
        }
      }),
    }))

    if (shouldPersistOnArrival) {
      scheduleAgentRuntimePersist(id, { immediate: true })
    }
  },

  // Active tasks (work mode)
  activeTasks: {},

  assignTask: (agentId, taskLabel) =>
    set((s) => {
      const route = resolveTaskWorkRoute(taskLabel)

      return {
        activeTasks: {
          ...s.activeTasks,
          [agentId]: {
            label: taskLabel,
            status: "running",
            startedAt: Date.now(),
            steps: [],
            result: null,
          },
        },
        agents: s.agents.map((a) =>
          a.id === agentId
            ? {
                ...a,
                mode: "work",
                currentTask: taskLabel,
                currentZoneId: route.zoneId || a.currentZoneId,
                state: route.state || "busy",
                targetPosition: route.targetPosition || a.position,
              }
            : a
        ),
      }
    }),

  addTaskStep: (agentId, step) => {
    let changed = false
    set((s) => {
      const task = s.activeTasks[agentId]
      if (!task) return {}
      changed = true
      return {
        activeTasks: {
          ...s.activeTasks,
          [agentId]: { ...task, steps: [...task.steps, step] },
        },
      }
    })
    if (changed) scheduleAgentRuntimePersist(agentId)
  },

  completeTask: (agentId, result) => {
    let completed = false
    set((s) => {
      const task = s.activeTasks[agentId]
      if (!task) return {}
      completed = true
      const finished = {
        ...task,
        status: "complete",
        result,
        error_details: null,
        completedAt: Date.now(),
      }
      const nextActive = { ...s.activeTasks }
      const nextToolUse = { ...s.activeToolUse }
      delete nextActive[agentId]
      delete nextToolUse[agentId]
      return {
        activeTasks: nextActive,
        activeToolUse: nextToolUse,
        completedTasks: capCompletedTasksPerAgent([
          finished,
          ...s.completedTasks,
        ]),
        activityLog: [
          ...s.activityLog,
          {
            id: makeActivityId(),
            timestamp: Date.now(),
            agentId,
            type: "task_complete",
            message: `Completed: ${task.label}`,
            emoji: "✓",
            meta: { taskId: task.id },
          },
        ].slice(-TASK_ACTIVITY_LIMIT),
      }
    })
    if (completed) {
      Promise.resolve().then(() =>
        useWorldStore.getState().resumeAgentLife(agentId)
      )
    }
  },

  failTask: (agentId, error, errorDetails = null) => {
    let failed = false
    set((s) => {
      const task = s.activeTasks[agentId]
      if (!task) return {}
      failed = true
      const finished = {
        ...task,
        status: "failed",
        error,
        error_details: errorDetails,
        completedAt: Date.now(),
      }
      const nextActive = { ...s.activeTasks }
      const nextToolUse = { ...s.activeToolUse }
      delete nextActive[agentId]
      delete nextToolUse[agentId]
      return {
        activeTasks: nextActive,
        activeToolUse: nextToolUse,
        completedTasks: capCompletedTasksPerAgent([
          finished,
          ...s.completedTasks,
        ]),
        activityLog: [
          ...s.activityLog,
          {
            id: makeActivityId(),
            timestamp: Date.now(),
            agentId,
            type: "task_fail",
            message: `Failed: ${task.label}`,
            emoji: "✗",
            meta: { taskId: task.id, error, error_details: errorDetails },
          },
        ].slice(-TASK_ACTIVITY_LIMIT),
      }
    })
    if (failed) {
      Promise.resolve().then(() =>
        useWorldStore.getState().resumeAgentLife(agentId)
      )
    }
  },

  // Tool use tracking
  activeToolUse: {},
  optimisticToolUse: {},
  optimisticTaskClosures: {},
  setAgentToolUse: (agentId, toolUseData) => {
    let resolvedAgentId = agentId
    set((s) => {
      resolvedAgentId = resolveWorldAgentId(agentId, s.agents) || agentId
      const nextToolUse = { ...s.activeToolUse, [resolvedAgentId]: toolUseData }
      if (agentId && agentId !== resolvedAgentId) {
        delete nextToolUse[agentId]
      }
      return { activeToolUse: nextToolUse }
    })
    scheduleAgentRuntimePersist(resolvedAgentId)
  },
  clearAgentToolUse: (agentId) => {
    let resolvedAgentId = agentId
    set((s) => {
      resolvedAgentId = resolveWorldAgentId(agentId, s.agents) || agentId
      const next = { ...s.activeToolUse }
      delete next[resolvedAgentId]
      if (agentId && agentId !== resolvedAgentId) {
        delete next[agentId]
      }
      return { activeToolUse: next }
    })
    scheduleAgentRuntimePersist(resolvedAgentId)
  },
  setOptimisticAgentToolUse: (agentId, toolUseData) =>
    set((s) => {
      const resolvedAgentId = resolveWorldAgentId(agentId, s.agents) || agentId
      const nextToolUse = {
        ...s.optimisticToolUse,
        [resolvedAgentId]: toolUseData,
      }
      if (agentId && agentId !== resolvedAgentId) {
        delete nextToolUse[agentId]
      }
      return { optimisticToolUse: nextToolUse }
    }),
  setOptimisticTaskClosure: (
    agentId,
    closureData = { finishedAt: Date.now() }
  ) =>
    set((s) => {
      const resolvedAgentId = resolveWorldAgentId(agentId, s.agents) || agentId
      const nextClosures = {
        ...s.optimisticTaskClosures,
        [resolvedAgentId]: closureData,
      }
      if (agentId && agentId !== resolvedAgentId) {
        delete nextClosures[agentId]
      }
      return { optimisticTaskClosures: nextClosures }
    }),
  clearOptimisticTaskClosure: (agentId) =>
    set((s) => {
      const resolvedAgentId = resolveWorldAgentId(agentId, s.agents) || agentId
      const next = { ...s.optimisticTaskClosures }
      delete next[resolvedAgentId]
      if (agentId && agentId !== resolvedAgentId) {
        delete next[agentId]
      }
      return { optimisticTaskClosures: next }
    }),
  clearOptimisticAgentToolUse: (agentId) =>
    set((s) => {
      const resolvedAgentId = resolveWorldAgentId(agentId, s.agents) || agentId
      const next = { ...s.optimisticToolUse }
      delete next[resolvedAgentId]
      if (agentId && agentId !== resolvedAgentId) {
        delete next[agentId]
      }
      return { optimisticToolUse: next }
    }),

  floatingWindowOrder: [],
  registerFloatingWindow: (windowId) =>
    set((s) => ({
      floatingWindowOrder: upsertFloatingWindowOrder(
        s.floatingWindowOrder || [],
        windowId
      ),
    })),
  unregisterFloatingWindow: (windowId) =>
    set((s) => ({
      floatingWindowOrder: removeFloatingWindowOrder(
        s.floatingWindowOrder || [],
        windowId
      ),
    })),
  focusFloatingWindow: (windowId) =>
    set((s) => ({
      floatingWindowOrder: upsertFloatingWindowOrder(
        s.floatingWindowOrder || [],
        windowId
      ),
    })),

  // Skill equip/unequip
  equipSkill: (agentId, skillId) =>
    set((s) => {
      const agent = s.agents.find((a) => a.id === agentId)
      if (!agent) return {}
      const skill = SKILLS.find((sk) => sk.id === skillId)
      if (!skill) return {}
      if (agent.equippedSkills?.includes(skillId)) return {}
      const used = getPointsUsed(agent)
      if (used + skill.cost > SKILL_POINT_BUDGET) return {}
      return {
        agents: s.agents.map((a) =>
          a.id === agentId
            ? { ...a, equippedSkills: [...(a.equippedSkills || []), skillId] }
            : a
        ),
      }
    }),

  unequipSkill: (agentId, skillId) =>
    set((s) => ({
      agents: s.agents.map((a) =>
        a.id === agentId
          ? {
              ...a,
              equippedSkills: (a.equippedSkills || []).filter(
                (id) => id !== skillId
              ),
            }
          : a
      ),
    })),

  // Open task result modals
  openResultModals: [],
  openResultModal: (task) =>
    set((s) => {
      const nextIdentity = getTaskIdentity(task)
      const windowId = getTaskResultWindowId(task)
      const existingIndex = s.openResultModals.findIndex(
        (entry) => getTaskIdentity(entry) === nextIdentity
      )

      if (existingIndex === -1) {
        return {
          openResultModals: [...s.openResultModals, task],
          floatingWindowOrder: upsertFloatingWindowOrder(
            s.floatingWindowOrder || [],
            windowId
          ),
        }
      }

      const openResultModals = [...s.openResultModals]
      openResultModals[existingIndex] = task
      return {
        openResultModals,
        floatingWindowOrder: upsertFloatingWindowOrder(
          s.floatingWindowOrder || [],
          windowId
        ),
      }
    }),
  closeResultModal: (task) =>
    set((s) => {
      const targetIdentity = getTaskIdentity(task)
      return {
        openResultModals: s.openResultModals.filter(
          (entry) => getTaskIdentity(entry) !== targetIdentity
        ),
        floatingWindowOrder: removeFloatingWindowOrder(
          s.floatingWindowOrder || [],
          getTaskResultWindowId(task)
        ),
      }
    }),

  // Agent travel state (globe view)
  agentTravel: {},

  startTravel: (agentId, fromCoords, toCoords, options = {}) => {
    set((s) => {
      const {
        placeName,
        travelActivity,
        routeGeometry,
        duration: routeDuration,
      } = options

      let duration
      if (routeDuration) {
        // Real duration from Directions API (seconds) → 2x speed for watchability
        // A 10-minute walk = 5 min real time
        duration = (routeDuration / 2) * 1000
        duration = Math.max(3000, Math.min(60000, duration))
      } else {
        const dist = Math.sqrt(
          Math.pow(fromCoords.lat - toCoords.lat, 2) +
            Math.pow(fromCoords.lng - toCoords.lng, 2)
        )
        duration = Math.max(3000, Math.min(15000, dist * 300000))
      }

      const travelEmoji =
        travelActivity === "driving"
          ? "🚗"
          : travelActivity === "cycling"
            ? "🚴"
            : "🚶"
      const destName = placeName || "destination"

      return {
        agentTravel: {
          ...s.agentTravel,
          [agentId]: {
            status: "traveling",
            fromCoords,
            toCoords,
            routeGeometry: routeGeometry || null,
            travelStartTime: Date.now(),
            travelDuration: duration,
            travelActivity: travelActivity || "walking",
            placeName: destName,
          },
        },
        activityLog: [
          ...s.activityLog,
          {
            id: makeActivityId(),
            timestamp: Date.now(),
            agentId,
            type: "zone_change",
            message: `${travelEmoji} Heading to ${destName}`,
            emoji: travelEmoji,
          },
        ].slice(-TASK_ACTIVITY_LIMIT),
      }
    })
    scheduleAgentRuntimePersist(agentId)
  },

  completeTravel: (agentId) =>
    set((s) => {
      const travel = s.agentTravel[agentId]
      if (!travel) return {}
      const next = { ...s.agentTravel }
      next[agentId] = { ...next[agentId], status: "idle" }
      return { agentTravel: next }
    }),

  // Agent indicators (pulsing icons above heads)
  agentIndicators: {},
  setAgentIndicator: (agentId, data) =>
    set((s) => ({
      agentIndicators: { ...s.agentIndicators, [agentId]: data },
    })),
  clearAgentIndicator: (agentId) =>
    set((s) => {
      const next = { ...s.agentIndicators }
      delete next[agentId]
      return { agentIndicators: next }
    }),

  // Chat state
  openChats: [],
  activeChatEmbeds: [],

  openChat: (agentId) =>
    set((s) => {
      const agent = s.agents.find((a) => a.id === agentId)
      const hasChatEmbed = Boolean(process.env.NEXT_PUBLIC_CHAT_APP_EMBED_URL)

      if (hasChatEmbed) {
        return {
          activeChatEmbeds: upsertAgentWindow(
            s.activeChatEmbeds || [],
            agentId,
            createChatEmbedWindow,
            (chatEmbed) => ({
              ...chatEmbed,
              minimized: false,
            })
          ),
          floatingWindowOrder: upsertFloatingWindowOrder(
            s.floatingWindowOrder || [],
            getChatEmbedWindowId(agentId)
          ),
        }
      }

      const existing = s.openChats.find((c) => c.agentId === agentId)
      if (existing) {
        return {
          openChats: s.openChats.map((c) =>
            c.agentId === agentId
              ? { ...c, minimized: false, closed: false }
              : c
          ),
          floatingWindowOrder: upsertFloatingWindowOrder(
            s.floatingWindowOrder || [],
            getTextChatWindowId(agentId)
          ),
        }
      }
      const greeting = `Hello, I'm ${agent?.name || "agent"}.`
      return {
        openChats: [
          ...s.openChats,
          {
            agentId,
            minimized: false,
            messages: [{ role: "agent", text: greeting, ts: Date.now() }],
          },
        ],
        floatingWindowOrder: upsertFloatingWindowOrder(
          s.floatingWindowOrder || [],
          getTextChatWindowId(agentId)
        ),
      }
    }),

  minimizeChat: (agentId) =>
    set((s) => ({
      openChats: s.openChats.map((c) =>
        c.agentId === agentId ? { ...c, minimized: !c.minimized } : c
      ),
    })),

  closeChat: (agentId) =>
    set((s) => ({
      openChats: s.openChats.map((c) =>
        c.agentId === agentId ? { ...c, minimized: true, closed: true } : c
      ),
      floatingWindowOrder: removeFloatingWindowOrder(
        s.floatingWindowOrder || [],
        getTextChatWindowId(agentId)
      ),
    })),

  closeChatEmbed: (agentId) =>
    set((s) => ({
      activeChatEmbeds: (s.activeChatEmbeds || []).filter(
        (chatEmbed) => chatEmbed.agentId !== agentId
      ),
      floatingWindowOrder: removeFloatingWindowOrder(
        s.floatingWindowOrder || [],
        getChatEmbedWindowId(agentId)
      ),
    })),

  toggleChatEmbedMinimized: (agentId) =>
    set((s) => ({
      activeChatEmbeds: updateAgentWindow(
        s.activeChatEmbeds || [],
        agentId,
        (chatEmbed) => ({ ...chatEmbed, minimized: !chatEmbed.minimized })
      ),
    })),

  toggleChatEmbedFullscreen: (agentId) =>
    set((s) => ({
      activeChatEmbeds: updateAgentWindow(
        s.activeChatEmbeds || [],
        agentId,
        (chatEmbed) => ({ ...chatEmbed, fullscreen: !chatEmbed.fullscreen })
      ),
    })),

  setChatEmbedPosition: (agentId, x, y) =>
    set((s) => ({
      activeChatEmbeds: updateAgentWindow(
        s.activeChatEmbeds || [],
        agentId,
        (chatEmbed) => ({ ...chatEmbed, position: { x, y } })
      ),
    })),

  setChatEmbedSize: (agentId, w, h) =>
    set((s) => ({
      activeChatEmbeds: updateAgentWindow(
        s.activeChatEmbeds || [],
        agentId,
        (chatEmbed) => ({ ...chatEmbed, size: { w, h } })
      ),
    })),

  addMessage: (agentId, msg) =>
    set((s) => ({
      openChats: s.openChats.map((c) =>
        c.agentId === agentId ? { ...c, messages: [...c.messages, msg] } : c
      ),
    })),

  appendToLastAgentMessage: (agentId, fullText) =>
    set((s) => ({
      openChats: s.openChats.map((chat) => {
        if (chat.agentId !== agentId || chat.messages.length === 0) return chat
        const messages = [...chat.messages]
        const lastIndex = messages.length - 1
        const lastMessage = messages[lastIndex]
        if (lastMessage.role !== "agent") return chat
        messages[lastIndex] = { ...lastMessage, text: fullText }
        return { ...chat, messages }
      }),
    })),

  finalizeLastAgentMessage: (agentId) =>
    set((s) => ({
      openChats: s.openChats.map((chat) => {
        if (chat.agentId !== agentId || chat.messages.length === 0) return chat
        const messages = [...chat.messages]
        const lastIndex = messages.length - 1
        const lastMessage = messages[lastIndex]
        if (lastMessage.role !== "agent") return chat
        const nextMessage = { ...lastMessage }
        delete nextMessage.streaming
        messages[lastIndex] = nextMessage
        return { ...chat, messages }
      }),
    })),

  replaceLastAgentMessage: (agentId, text) =>
    set((s) => ({
      openChats: s.openChats.map((chat) => {
        if (chat.agentId !== agentId || chat.messages.length === 0) return chat
        const messages = [...chat.messages]
        const lastIndex = messages.length - 1
        const lastMessage = messages[lastIndex]
        if (lastMessage.role !== "agent") return chat
        messages[lastIndex] = { ...lastMessage, text, streaming: false }
        return { ...chat, messages }
      }),
    })),

  setTyping: (agentId, isTyping) =>
    set((s) => ({
      openChats: s.openChats.map((c) =>
        c.agentId === agentId ? { ...c, isTyping } : c
      ),
    })),

  // Scene generation cache
  locationScenes: {},

  setSceneGenerating: (locationTag, id) =>
    set((s) => ({
      locationScenes: {
        ...s.locationScenes,
        [locationTag]: { id, status: "generating" },
      },
    })),

  setSceneReady: (locationTag, sceneData) =>
    set((s) => ({
      locationScenes: {
        ...s.locationScenes,
        [locationTag]: { ...sceneData, status: "ready" },
      },
    })),

  setSceneUnavailable: (locationTag, reason = "unavailable") =>
    set((s) => ({
      locationScenes: {
        ...s.locationScenes,
        [locationTag]: {
          ...(s.locationScenes[locationTag] || {}),
          status: "unavailable",
          reason,
        },
      },
    })),

  clearScene: (locationTag) =>
    set((s) => {
      const nextScenes = { ...s.locationScenes }
      delete nextScenes[locationTag]
      return { locationScenes: nextScenes }
    }),

  // Life sim state per agent
  lifeSimState: {},

  applyBootstrap: (bootstrap, options = {}) => {
    const agentsToPersist = new Set()
    installLifeSimCatalog(bootstrap?.life_sim_catalog || null)

    set((s) => {
      const stableOnly = options.stableOnly === true
      const preserveRuntime =
        (stableOnly || options.preserveRuntime === true) &&
        Array.isArray(s.agents) &&
        s.agents.length > 0
      const currentAgentsById = new Map(
        (s.agents || []).map((agent) => [agent.id, agent])
      )
      const nextAgents = (bootstrap?.agents || []).map((agent, idx) => {
        const profile = agent.profile || {}
        const world = agent.world || {}
        const taskState = agent.tasks || {}
        const platform = agent.platform || {}
        const characterConfig = profile.character_config || {}
        const home = profile.home || {}
        const equipment = profile.equipment || {}
        const displayName =
          characterConfig.name || platform.name || agent.openclaw_agent_id
        const resolvedConfig = {
          name: displayName,
          personality_prompt: characterConfig.personality_prompt || "",
          backstory_prompt: characterConfig.backstory_prompt || "",
          quirks_and_mannerisms: characterConfig.quirks_and_mannerisms || "",
          beliefs_and_intents: characterConfig.beliefs_and_intents || "",
          verbosity: characterConfig.verbosity || "medium",
          response_max_characters:
            characterConfig.response_max_characters || 400,
        }
        const derivedStats = deriveStats(
          resolvedConfig,
          agent.openclaw_agent_id
        )

        const bootstrapAgent = {
          id: agent.openclaw_agent_id,
          name: displayName,
          openclawAgentId: agent.openclaw_agent_id,
          openclawAgentName: displayName,
          chatAppCharacterId: profile.chat_app_character_id || null,
          chatAppCharacterProfile: profile.chat_app_character_profile || null,
          characterConfig: resolvedConfig,
          profileDoc: profile,
          role: profile.role_override || platform.role || "Agent",
          color: profile.color || "#53b1fd",
          homeDistrict: profile.district_id || "residential_north",
          homeCity: home.city || "",
          homeCoords: home.coords || null,
          homeBbox: home.bbox || null,
          personalPlaces: profile.personal_places || null,
          skills: profile.skills || [],
          equippedSkills: profile.equipped_skills || [
            "web_search",
            "write_note",
          ],
          openclawSkills: Array.isArray(agent.skills) ? agent.skills : [],
          stats: derivedStats,
          personality: {
            trait: deriveTrait(resolvedConfig),
            tags: deriveTags(resolvedConfig),
          },
          lifeSim: profile.life_sim || {
            baseActivities: ["eating", "sleep", "reading", "social"],
          },
          schedule: profile.schedule || {
            wakeTime: 7,
            weekdaySlots: null,
            weekendSlots: null,
          },
          preferences: profile.preferences || {
            socialEnergy: derivedStats.speed / 100,
            workEthic: derivedStats.reliability / 100,
            exercise: derivedStats.speed / 100,
            creativity: derivedStats.creativity / 100,
          },
          autonomyLevel: profile.autonomyLevel ?? 0,
          directives: profile.directives || {
            mission: "",
            behavioral: "",
            constraints: "",
          },
          equipment,
          position: world.position || [idx * 3 - 3, 0, 0],
          targetPosition: world.target_position ||
            world.position || [idx * 3 - 3, 0, 0],
          state: world.state || (taskState.active ? "busy" : "idle"),
          currentTask: world.current_task || taskState.active?.label || null,
          currentZoneId: world.current_zone_id || null,
          mode: world.mode || (taskState.active ? "work" : "life"),
          activity: world.activity || null,
          locationTag: world.location_tag || null,
        }
        return preserveRuntime
          ? preserveAgentRuntimeFields(
              bootstrapAgent,
              currentAgentsById.get(bootstrapAgent.id)
            )
          : bootstrapAgent
      })
      const nextAgentIndexes = new Map(
        nextAgents.map((agent, index) => [agent.id, index])
      )

      const nextLifeSim = {}
      const nextActiveTasks = {}
      const nextTaskQueues = {}
      const nextCompletedTasks = []
      const nextActivityLog = []
      const nextActiveToolUse = {}
      ;(bootstrap?.agents || []).forEach((agent) => {
        const ls = agent.life_sim || {}
        const taskState = agent.tasks || {}
        const agentId = agent.openclaw_agent_id
        const fired = Array.isArray(ls.fired_today)
          ? new Set(ls.fired_today)
          : new Set()
        nextLifeSim[agentId] = {
          needs: ls.needs || initNeeds(),
          firedToday: fired,
          simMin:
            ls.sim_min ?? new Date().getHours() * 60 + new Date().getMinutes(),
          dayNum: ls.day_num ?? 0,
          lastActId: ls.last_act_id ?? null,
          actEndMin: ls.act_end_min ?? 0,
        }

        const activeTask = normalizeBootstrapTask(
          taskState.active,
          agentId,
          "running"
        )
        if (activeTask) {
          nextActiveTasks[agentId] = activeTask
        }

        const queuedTasks = Array.isArray(taskState.queue)
          ? taskState.queue
              .map((task) => normalizeBootstrapTask(task, agentId, "queued"))
              .filter(Boolean)
          : []
        if (queuedTasks.length > 0) {
          nextTaskQueues[agentId] = queuedTasks
        }

        const completedTasks = Array.isArray(taskState.completed)
          ? taskState.completed
              .map((task) =>
                normalizeBootstrapTask(
                  task,
                  agentId,
                  task?.status || "complete"
                )
              )
              .filter(Boolean)
          : []
        nextCompletedTasks.push(...completedTasks)

        if (taskState.tool_use) {
          nextActiveToolUse[agentId] = taskState.tool_use
        }

        const activityEntries = Array.isArray(taskState.activity_log)
          ? taskState.activity_log.map((entry) => ({
              ...entry,
              id: safeActivityId(entry.id),
              agentId: entry.agentId || agentId,
              timestamp: entry.timestamp || Date.now(),
            }))
          : []
        nextActivityLog.push(...activityEntries)

        if (!stableOnly && !taskState.active && queuedTasks.length === 0) {
          const agentIndex = nextAgentIndexes.get(agentId)
          const runtimeAgent =
            agentIndex !== undefined ? nextAgents[agentIndex] : null
          const backfill = synthesizeBootstrapLifeBackfill({
            agent: runtimeAgent,
            world: agent.world || {},
            lifeSim: ls,
            activityLog: activityEntries,
            describeActivity: (entry, resolved) =>
              generateFlavorText(entry, resolved),
          })
          if (backfill?.changed) {
            nextLifeSim[agentId] = buildRuntimeLifeSimState(backfill.lifeSim)
            nextActivityLog.push(...backfill.entries)
            agentsToPersist.add(agentId)
            if (agentIndex !== undefined && runtimeAgent) {
              nextAgents[agentIndex] = {
                ...runtimeAgent,
                mode: backfill.world.mode || runtimeAgent.mode,
                state: backfill.world.state || runtimeAgent.state,
                currentTask: backfill.world.current_task || null,
                currentZoneId:
                  backfill.world.current_zone_id || runtimeAgent.currentZoneId,
                activity: backfill.world.activity || null,
                locationTag: backfill.world.location_tag || null,
              }
            }
          }
        }
      })

      const worldState = bootstrap?.world_state || {}
      const bootstrapUiState = resolveBootstrapUiState({
        currentState: s,
        worldState,
        preserveRuntime,
      })

      if (stableOnly) {
        return {
          agents: nextAgents,
          activeView: bootstrapUiState.activeView,
          selectedAgentId: bootstrapUiState.selectedAgentId,
          mapZoomTarget: bootstrapUiState.mapZoomTarget,
        }
      }

      return {
        agents: nextAgents,
        lifeSimState: nextLifeSim,
        activeTasks: nextActiveTasks,
        taskQueues: nextTaskQueues,
        completedTasks: capCompletedTasksPerAgent(nextCompletedTasks),
        activeToolUse: nextActiveToolUse,
        activityLog: mergeActivityLogEntries(nextActivityLog),
        activeView: bootstrapUiState.activeView,
        selectedAgentId: bootstrapUiState.selectedAgentId,
        mapZoomTarget: bootstrapUiState.mapZoomTarget,
        optimisticToolUse: {},
        optimisticTaskClosures: {},
      }
    })

    if (agentsToPersist.size > 0) {
      Promise.resolve().then(() => {
        agentsToPersist.forEach((agentId) => {
          scheduleAgentRuntimePersist(agentId, { immediate: true })
        })
      })
    }
  },

  applyAgentTaskSnapshot: (agentId, snapshot) => {
    let shouldResumeLife = false
    set((s) => {
      const resolvedAgentId = resolveWorldAgentId(agentId, s.agents)
      if (!resolvedAgentId) {
        return {}
      }

      const previousActiveTask = s.activeTasks[resolvedAgentId] || null
      const taskState =
        snapshot?.tasks && typeof snapshot.tasks === "object"
          ? snapshot.tasks
          : null
      const world =
        snapshot?.world && typeof snapshot.world === "object"
          ? snapshot.world
          : null
      const lifeSim =
        snapshot?.life_sim && typeof snapshot.life_sim === "object"
          ? snapshot.life_sim
          : null
      const currentAgent =
        s.agents.find((agent) => agent.id === resolvedAgentId) || null
      const currentLifeSim = s.lifeSimState[resolvedAgentId] || null
      const snapshotMode =
        typeof world?.mode === "string" && world.mode.trim().length > 0
          ? world.mode
          : null
      const snapshotHasQueuedTasks =
        Array.isArray(taskState?.queue) && taskState.queue.length > 0
      const snapshotHasWorldActivity =
        typeof world?.activity === "string" && world.activity.trim().length > 0
      const snapshotHasLifeSimActivity =
        typeof lifeSim?.last_act_id === "string" &&
        lifeSim.last_act_id.trim().length > 0
      const snapshotLooksIdleLife =
        !taskState?.active &&
        !snapshotHasQueuedTasks &&
        (!snapshotMode || snapshotMode === "life") &&
        !(
          typeof world?.current_task === "string" &&
          world.current_task.trim().length > 0
        )
      const snapshotActivityLog = Array.isArray(taskState?.activity_log)
        ? taskState.activity_log
        : []
      const latestSnapshotActivity =
        snapshotActivityLog.length > 0
          ? snapshotActivityLog[snapshotActivityLog.length - 1]
          : null
      const latestSnapshotActivityType =
        typeof latestSnapshotActivity?.type === "string"
          ? latestSnapshotActivity.type
          : null
      const snapshotHasStaleLifeSimActivity =
        snapshotLooksIdleLife &&
        !snapshotHasWorldActivity &&
        snapshotHasLifeSimActivity &&
        (latestSnapshotActivityType === "task_complete" ||
          latestSnapshotActivityType === "task_fail" ||
          Boolean(previousActiveTask) ||
          currentAgent?.mode === "work" ||
          (typeof currentAgent?.currentTask === "string" &&
            currentAgent.currentTask.trim().length > 0))
      const snapshotHasLifeActivity =
        snapshotHasWorldActivity ||
        (snapshotHasLifeSimActivity && !snapshotHasStaleLifeSimActivity)
      const localHasLifeActivity =
        currentAgent?.mode === "life" &&
        ((typeof currentAgent?.activity === "string" &&
          currentAgent.activity.trim().length > 0) ||
          (typeof currentLifeSim?.lastActId === "string" &&
            currentLifeSim.lastActId.trim().length > 0))

      let effectiveWorld = world
      let effectiveLifeSim = lifeSim

      if (snapshotHasStaleLifeSimActivity) {
        effectiveLifeSim = {
          ...(currentLifeSim
            ? buildPersistedLifeSimSnapshot(currentLifeSim)
            : {}),
          ...(lifeSim || {}),
          last_act_id: null,
          act_end_min: 0,
        }
      }

      if (snapshotLooksIdleLife && !snapshotHasLifeActivity) {
        if (localHasLifeActivity) {
          effectiveWorld = {
            ...(world || {}),
            mode: "life",
            state: currentAgent?.state || world?.state || "idle",
            current_task: currentAgent?.currentTask || null,
            current_zone_id:
              world?.current_zone_id || currentAgent?.currentZoneId || "life",
            activity: currentAgent?.activity || null,
            location_tag: currentAgent?.locationTag || null,
          }
          effectiveLifeSim = currentLifeSim
            ? buildPersistedLifeSimSnapshot(currentLifeSim)
            : lifeSim
        } else {
          effectiveLifeSim = {
            ...(currentLifeSim
              ? buildPersistedLifeSimSnapshot(currentLifeSim)
              : {}),
            ...(lifeSim || {}),
            last_act_id: null,
            act_end_min: 0,
          }
        }
      }

      let nextAgents = s.agents
      if (taskState || effectiveWorld) {
        nextAgents = s.agents.map((agent) =>
          agent.id === resolvedAgentId
            ? applyRuntimeSnapshotToAgent(agent, taskState, effectiveWorld)
            : agent
        )
      }

      const nextState = {
        ...(nextAgents !== s.agents ? { agents: nextAgents } : {}),
      }

      if (effectiveLifeSim) {
        nextState.lifeSimState = {
          ...s.lifeSimState,
          [resolvedAgentId]: buildRuntimeLifeSimState(effectiveLifeSim),
        }
      }

      if (!taskState) {
        return nextState
      }

      const activeTask = normalizeBootstrapTask(
        taskState.active,
        resolvedAgentId,
        "running"
      )
      const queuedTasks = Array.isArray(taskState.queue)
        ? taskState.queue
            .map((task) =>
              normalizeBootstrapTask(task, resolvedAgentId, "queued")
            )
            .filter(Boolean)
        : []
      // Only treat completed as authoritative when the snapshot actually
      // includes the field. Partial snapshots (PATCH /world/agent runtime
      // updates) deliberately omit `completed` because history is DB-owned,
      // so falling back to `[]` here would wipe the local list.
      const snapshotHasCompleted = Array.isArray(taskState.completed)
      const completedTasks = snapshotHasCompleted
        ? taskState.completed
            .map((task) =>
              normalizeBootstrapTask(
                task,
                resolvedAgentId,
                task?.status || "complete"
              )
            )
            .filter(Boolean)
        : null
      const activityEntries = buildRuntimeActivityLog(
        taskState,
        resolvedAgentId
      )

      const nextActiveTasks = { ...s.activeTasks }
      if (activeTask) {
        nextActiveTasks[resolvedAgentId] = activeTask
      } else {
        delete nextActiveTasks[resolvedAgentId]
      }
      if (agentId && agentId !== resolvedAgentId) {
        delete nextActiveTasks[agentId]
      }

      const nextTaskQueues = { ...s.taskQueues }
      if (queuedTasks.length > 0) {
        nextTaskQueues[resolvedAgentId] = queuedTasks
      } else {
        delete nextTaskQueues[resolvedAgentId]
      }
      if (agentId && agentId !== resolvedAgentId) {
        delete nextTaskQueues[agentId]
      }

      const nextActiveToolUse = { ...s.activeToolUse }
      if (taskState.tool_use) {
        nextActiveToolUse[resolvedAgentId] = taskState.tool_use
      } else {
        delete nextActiveToolUse[resolvedAgentId]
      }
      if (agentId && agentId !== resolvedAgentId) {
        delete nextActiveToolUse[agentId]
      }

      nextState.activeTasks = nextActiveTasks
      nextState.taskQueues = nextTaskQueues
      nextState.activeToolUse = nextActiveToolUse
      if (
        !activeTask &&
        (s.optimisticTaskClosures?.[resolvedAgentId] ||
          (agentId && s.optimisticTaskClosures?.[agentId]))
      ) {
        const nextOptimisticTaskClosures = { ...s.optimisticTaskClosures }
        delete nextOptimisticTaskClosures[resolvedAgentId]
        if (agentId && agentId !== resolvedAgentId) {
          delete nextOptimisticTaskClosures[agentId]
        }
        nextState.optimisticTaskClosures = nextOptimisticTaskClosures
      }
      if (
        s.optimisticToolUse?.[resolvedAgentId] ||
        (agentId && s.optimisticToolUse?.[agentId])
      ) {
        const nextOptimisticToolUse = { ...s.optimisticToolUse }
        delete nextOptimisticToolUse[resolvedAgentId]
        if (agentId && agentId !== resolvedAgentId) {
          delete nextOptimisticToolUse[agentId]
        }
        nextState.optimisticToolUse = nextOptimisticToolUse
      }
      if (completedTasks !== null) {
        const preservedOthers = s.completedTasks.filter(
          (task) => task.agentId !== resolvedAgentId && task.agentId !== agentId
        )
        const combined = [...preservedOthers, ...completedTasks]
        nextState.completedTasks = capCompletedTasksPerAgent(combined)

        if (process.env.NODE_ENV !== "production") {
          const before = s.completedTasks.filter(
            (task) =>
              task.agentId === resolvedAgentId || task.agentId === agentId
          ).length
          const after = completedTasks.length
          if (before !== after) {
            console.debug(
              "[world-store] applyAgentTaskSnapshot replaced completed for",
              resolvedAgentId,
              "-",
              before,
              "→",
              after,
              "entries"
            )
          }
        }
      }
      // Activity log is also append-only and DB-owned; only merge new entries
      // from the snapshot, never drop the existing in-memory entries for this
      // agent based on a partial runtime snapshot.
      nextState.activityLog = mergeActivityLogEntries(
        s.activityLog,
        activityEntries
      )

      shouldResumeLife = shouldResumeLifeFromTaskSnapshot({
        previousActiveTask,
        nextActiveTask: activeTask,
        queuedTasks,
        world: effectiveWorld,
        lifeSim: effectiveLifeSim,
      })

      return nextState
    })

    if (shouldResumeLife) {
      Promise.resolve().then(() => {
        const state = useWorldStore.getState()
        const canonicalAgentId =
          resolveWorldAgentId(agentId, state.agents) || agentId
        const agent = state.agents.find(
          (entry) => entry.id === canonicalAgentId
        )
        if (
          !shouldResumeLifeFromRuntime({
            activeTask: state.activeTasks[canonicalAgentId] || null,
            queuedTasks: state.taskQueues[canonicalAgentId] || [],
            agent,
            lifeSim: state.lifeSimState[canonicalAgentId] || null,
          })
        ) {
          return
        }

        state.resumeAgentLife(canonicalAgentId)
      })
    }
  },

  tickAgentLifeSim: (agentId, deltaMin) => {
    let persistMode = null
    set((s) => {
      const agent = s.agents.find((a) => a.id === agentId)
      if (!agent) return {}

      const ls = s.lifeSimState[agentId]
      if (!ls) return {}

      const newSimMin = ls.simMin + deltaMin
      const hour = Math.floor((newSimMin % 1440) / 60)
      const dow = simDow(ls.dayNum)
      const weekend = isWeekendDow(dow)

      // Only pick a new activity if current one has expired
      const actExpired = !ls.lastActId || newSimMin >= (ls.actEndMin || 0)

      let newActId = ls.lastActId
      let newFired = ls.firedToday
      let newActEndMin = ls.actEndMin || 0

      if (actExpired) {
        // Check for slot-based activity
        const slots = weekend
          ? (agent.schedule?.weekendSlots ?? getDefaultSlots(true))
          : (agent.schedule?.weekdaySlots ?? getDefaultSlots(false))
        const slotMatch = pickSlot(slots, ls.firedToday, newSimMin)

        if (slotMatch) {
          newActId = slotMatch.actId
          newFired = new Set(ls.firedToday)
          newFired.add(slotMatch.id)
          // Duration from slot
          const dur =
            slotMatch.durMin +
            Math.random() * (slotMatch.durMax - slotMatch.durMin)
          newActEndMin = newSimMin + dur
        } else {
          // Score float activities
          const pool = getActPool(agent, weekend)
          const needs = ls.needs
          const picked = weightedPick(pool, (act) =>
            scoreFloat(act, agent, hour, needs, ls.lastActId, weekend)
          )
          if (picked) {
            const resolved = resolveAct(picked)
            newActId =
              resolved?.id ?? (typeof picked === "string" ? picked : picked.id)
            // Duration from activity definition
            const durRange = resolved?.dur || [20, 40]
            const dur =
              durRange[0] + Math.random() * (durRange[1] - durRange[0])
            newActEndMin = newSimMin + dur
          }
        }
      }

      const newNeeds = tickNeeds(ls.needs, newActId, deltaMin)
      const actChanged = newActId !== ls.lastActId

      // Build updated life sim state
      const newLS = {
        ...s.lifeSimState,
        [agentId]: {
          needs: newNeeds,
          firedToday: newFired,
          simMin: newSimMin,
          dayNum: ls.dayNum,
          lastActId: newActId,
          actEndMin: newActEndMin,
        },
      }

      // If activity changed, update the agent's task/activity fields
      if (actChanged && newActId) {
        persistMode = "immediate"
        const resolved = resolveAct(newActId)
        const flavorText = generateFlavorText(agent, resolved)

        const placeKey = getActivityPlaceKey(newActId) || "home"
        const newLocationTag = `${placeKey}_${newActId}`

        const currentPlaceKey = (agent.locationTag || "home_idle").split("_")[0]
        let travelUpdate = {}
        if (agent.personalPlaces && placeKey !== currentPlaceKey) {
          const fromPlace =
            agent.personalPlaces[currentPlaceKey] || agent.personalPlaces.home
          const toPlace =
            agent.personalPlaces[placeKey] || agent.personalPlaces.home
          if (
            fromPlace &&
            toPlace &&
            (fromPlace.lat !== toPlace.lat || fromPlace.lng !== toPlace.lng)
          ) {
            travelUpdate = {
              _pendingTravel: {
                fromCoords: { lat: fromPlace.lat, lng: fromPlace.lng },
                toCoords: { lat: toPlace.lat, lng: toPlace.lng },
                placeName: toPlace.name || placeKey,
              },
            }
          }
        } else if (!agent.personalPlaces) {
          const locType =
            resolved?.cat === "rest" || resolved?.cat === "routine"
              ? "home"
              : resolved?.cat === "active" || resolved?.cat === "leisure"
                ? "recreation"
                : resolved?.cat === "social"
                  ? "social"
                  : resolved?.cat === "productive"
                    ? "commercial"
                    : "home"
          const resolver = LOCATION_TYPE_TO_DISTRICT[locType]
          const newDistrictId =
            typeof resolver === "function"
              ? resolver(agentId)
              : resolver?.() || "residential_north"
          const currentTag = agent.locationTag || ""
          const currentDistrictId = DISTRICTS.find((d) =>
            currentTag.startsWith(d.id + "_")
          )?.id
          if (currentDistrictId && currentDistrictId !== newDistrictId) {
            travelUpdate = {
              _pendingTravel: {
                from: currentDistrictId,
                to: newDistrictId,
                mode: "walking",
              },
            }
          }
        }

        const newAgents = s.agents.map((a) =>
          a.id === agentId
            ? {
                ...a,
                currentTask: flavorText,
                activity: newActId,
                mode: "life",
                locationTag: newLocationTag,
                ...travelUpdate,
              }
            : a
        )
        const lifeEntry = {
          id: makeActivityId(),
          timestamp: Date.now(),
          agentId,
          type: "life_sim",
          message: resolved?.name
            ? `Now ${resolved.name.toLowerCase()}`
            : `Now ${newActId}`,
          emoji: resolved?.emoji || "●",
          meta: { activityId: newActId },
        }
        return {
          lifeSimState: newLS,
          agents: newAgents,
          activityLog: mergeActivityLogEntries(s.activityLog, [lifeEntry]),
        }
      }

      if (actChanged) {
        persistMode = "debounced"
      }
      return { lifeSimState: newLS }
    })
    if (persistMode === "immediate") {
      scheduleAgentRuntimePersist(agentId, { immediate: true })
    } else if (persistMode === "debounced") {
      scheduleAgentRuntimePersist(agentId)
    }
  },

  resetDaySlots: (agentId) =>
    set((s) => {
      const now = new Date()
      const currentMin = now.getHours() * 60 + now.getMinutes()
      return {
        lifeSimState: {
          ...s.lifeSimState,
          [agentId]: {
            ...s.lifeSimState[agentId],
            firedToday: new Set(),
            dayNum: (s.lifeSimState[agentId]?.dayNum ?? 0) + 1,
            simMin: currentMin,
          },
        },
      }
    }),

  setAgentSchedule: (agentId, schedule) =>
    set((s) => ({
      agents: s.agents.map((a) =>
        a.id === agentId
          ? {
              ...a,
              schedule: {
                ...a.schedule,
                weekdaySlots: schedule.weekdaySlots ?? a.schedule?.weekdaySlots,
                weekendSlots: schedule.weekendSlots ?? a.schedule?.weekendSlots,
              },
              weekendActivities:
                schedule.weekendActivities ?? a.weekendActivities,
            }
          : a
      ),
    })),

  resumeAgentLife: (agentId) => {
    let resumed = false
    set((s) => {
      const agent = s.agents.find((entry) => entry.id === agentId)
      const lifeSim = s.lifeSimState[agentId]
      if (!agent || !lifeSim) return {}
      resumed = true
      const now = new Date()
      const currentMin = now.getHours() * 60 + now.getMinutes()
      return {
        agents: s.agents.map((entry) =>
          entry.id === agentId
            ? {
                ...entry,
                mode: "life",
                state: "idle",
                currentTask: null,
                currentZoneId: "life",
                activity: null,
                locationTag: null,
              }
            : entry
        ),
        lifeSimState: {
          ...s.lifeSimState,
          [agentId]: {
            ...lifeSim,
            simMin: currentMin,
            lastActId: null,
            actEndMin: 0,
          },
        },
      }
    })
    if (!resumed) return
    Promise.resolve().then(() => {
      const state = useWorldStore.getState()
      state.tickAgentLifeSim(agentId, 0.01)
      scheduleAgentRuntimePersist(agentId, { immediate: true })
    })
  },

  // Activity feed
  activityFeedVisible: false,
  toggleActivityFeed: () =>
    set((s) => ({ activityFeedVisible: !s.activityFeedVisible })),

  activityLog: [],
  logActivity: (event) => {
    set((s) => ({
      activityLog: mergeActivityLogEntries(s.activityLog, [
        {
          ...event,
          id: event.id || makeActivityId(),
          timestamp: event.timestamp || Date.now(),
        },
      ]),
    }))
    if (event?.agentId) {
      scheduleAgentRuntimePersist(event.agentId, { immediate: true })
    }
  },

  // Legacy compat (some files still reference these)
  taskLog: [],
  addTaskLog: (entry) => {
    set((s) => {
      // Also log to activity feed
      const type = entry.state === "transition" ? "zone_change" : "life_sim"
      return {
        taskLog: [entry, ...s.taskLog].slice(0, 50),
        activityLog: mergeActivityLogEntries(s.activityLog, [
          {
            id: safeActivityId(entry.id),
            timestamp: entry.timestamp || Date.now(),
            agentId: entry.agentId,
            type,
            message: entry.task,
            emoji: entry.zoneIcon || "●",
            meta: { zoneId: entry.zoneId },
          },
        ]),
      }
    })
    if (entry?.agentId) {
      scheduleAgentRuntimePersist(entry.agentId, { immediate: true })
    }
  },
  updateTaskLog: (logId, patch) =>
    set((s) => ({
      taskLog: s.taskLog.map((e) => (e.id === logId ? { ...e, ...patch } : e)),
    })),

  // Task Manager
  taskManagerOpen: false,
  toggleTaskManager: () =>
    set((s) => ({ taskManagerOpen: !s.taskManagerOpen })),

  activeDroppedTaskComposer: null,
  openDroppedTaskComposer: ({ agentId, zoneId, position, restoreSnapshot }) =>
    set({
      activeDroppedTaskComposer: {
        agentId,
        zoneId,
        position: position || { x: 0, y: 0 },
        restoreSnapshot: restoreSnapshot || null,
      },
      hoveredAgentId: agentId,
    }),
  closeDroppedTaskComposer: ({ restore = false } = {}) =>
    set((s) => {
      const composer = s.activeDroppedTaskComposer
      if (!composer) {
        return {}
      }

      const nextState = {
        activeDroppedTaskComposer: null,
        hoveredAgentId:
          s.hoveredAgentId === composer.agentId ? null : s.hoveredAgentId,
      }

      if (!restore || !composer.restoreSnapshot) {
        return nextState
      }

      const snapshot = composer.restoreSnapshot
      return {
        ...nextState,
        agents: s.agents.map((agent) =>
          agent.id === composer.agentId
            ? {
                ...agent,
                ...snapshot,
                position: Array.isArray(snapshot.position)
                  ? [...snapshot.position]
                  : agent.position,
                targetPosition: Array.isArray(snapshot.targetPosition)
                  ? [...snapshot.targetPosition]
                  : (snapshot.targetPosition ?? agent.targetPosition),
              }
            : agent
        ),
      }
    }),

  showCreator: false,
  setShowCreator: (v) => set({ showCreator: v }),

  overlayOpen: false,
  setOverlayOpen: (v) => set({ overlayOpen: v }),

  taskQueues: {},
  completedTasks: [],

  enqueueTask: (agentId, instruction, label) => {
    set((s) => {
      const task = {
        id: `task-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
        agentId,
        instruction,
        label,
        status: "queued",
        createdAt: Date.now(),
        startedAt: null,
        completedAt: null,
        steps: [],
        result: null,
        error: null,
        error_details: null,
      }
      const queue = [...(s.taskQueues[agentId] || []), task]
      const newQueues = { ...s.taskQueues, [agentId]: queue }

      // Log activity
      const newLog = [
        ...s.activityLog,
        {
          id: makeActivityId(),
          timestamp: Date.now(),
          agentId,
          type: "task_queue",
          message: `Task queued: ${label}`,
          emoji: "⋯",
          meta: { taskId: task.id },
        },
      ].slice(-TASK_ACTIVITY_LIMIT)

      // If no active task, start immediately
      const active = s.activeTasks?.[agentId]
      if (!active || active.status !== "running") {
        // Will be started by startNextTask called after set
        return { taskQueues: newQueues, activityLog: newLog }
      }
      return { taskQueues: newQueues, activityLog: newLog }
    })
    scheduleAgentRuntimePersist(agentId)
  },

  startNextTask: (agentId) => {
    let started = false
    let startedAgentId = agentId
    set((s) => {
      const resolvedAgentId = resolveWorldAgentId(agentId, s.agents) || agentId
      startedAgentId = resolvedAgentId
      const queue = s.taskQueues[resolvedAgentId] || []
      if (queue.length === 0) return {}
      const [next, ...rest] = queue
      const running = { ...next, status: "running", startedAt: Date.now() }
      started = true
      const route = resolveTaskWorkRoute(next.label || next.instruction || "")
      const nextQueues = { ...s.taskQueues, [resolvedAgentId]: rest }
      const nextActiveTasks = { ...s.activeTasks, [resolvedAgentId]: running }
      if (agentId && agentId !== resolvedAgentId) {
        delete nextQueues[agentId]
        delete nextActiveTasks[agentId]
      }

      return {
        taskQueues: nextQueues,
        activeTasks: nextActiveTasks,
        activityLog: [
          ...s.activityLog,
          {
            id: makeActivityId(),
            timestamp: Date.now(),
            agentId: resolvedAgentId,
            type: "task_start",
            message: `Started: ${next.label}`,
            emoji: "⚡",
            meta: { taskId: next.id },
          },
        ].slice(-TASK_ACTIVITY_LIMIT),
        agents: s.agents.map((a) =>
          a.id === resolvedAgentId
            ? {
                ...a,
                mode: "work",
                currentTask: next.label,
                currentZoneId: route.zoneId || a.currentZoneId,
                state: route.state || "busy",
                targetPosition: route.targetPosition || a.position,
              }
            : a
        ),
      }
    })
    if (started) {
      scheduleAgentRuntimePersist(startedAgentId)
    }
  },

  deleteQueuedTask: (taskId) => {
    const affectedAgents = []
    set((s) => {
      const newQueues = {}
      for (const [aid, queue] of Object.entries(s.taskQueues)) {
        const filtered = queue.filter((t) => t.id !== taskId)
        if (filtered.length !== queue.length) {
          affectedAgents.push(aid)
        }
        newQueues[aid] = filtered
      }
      return { taskQueues: newQueues }
    })
    affectedAgents.forEach((agentId) => scheduleAgentRuntimePersist(agentId))
  },

  editQueuedTask: (taskId, newInstruction, newLabel) => {
    const affectedAgents = []
    set((s) => {
      const newQueues = {}
      for (const [aid, queue] of Object.entries(s.taskQueues)) {
        newQueues[aid] = queue.map((t) =>
          t.id === taskId
            ? { ...t, instruction: newInstruction, label: newLabel }
            : t
        )
        if (newQueues[aid].some((task) => task.id === taskId)) {
          affectedAgents.push(aid)
        }
      }
      return { taskQueues: newQueues }
    })
    affectedAgents.forEach((id) => scheduleAgentRuntimePersist(id))
  },

  reorderQueue: (agentId, fromIndex, toIndex) => {
    set((s) => {
      const queue = [...(s.taskQueues[agentId] || [])]
      const [item] = queue.splice(fromIndex, 1)
      if (item) queue.splice(toIndex, 0, item)
      return { taskQueues: { ...s.taskQueues, [agentId]: queue } }
    })
    scheduleAgentRuntimePersist(agentId)
  },

  // Task toasts
  taskToasts: [],
  addTaskToast: (text, agentColor) =>
    set((s) => ({
      taskToasts: [
        ...s.taskToasts,
        {
          id: `toast-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
          text,
          agentColor,
          createdAt: Date.now(),
        },
      ],
    })),
  removeTaskToast: (id) =>
    set((s) => ({ taskToasts: s.taskToasts.filter((t) => t.id !== id) })),

  // Voice mode
  voiceMode: "ptt",
  toggleVoiceMode: () =>
    set((s) => ({ voiceMode: s.voiceMode === "ptt" ? "open" : "ptt" })),

  // Agent management
  addAgent: (agentData) =>
    set((s) => {
      const id = `agent-${Date.now()}`
      const lifeZone = ZONES.find((z) => z.id === "life")
      const startPos = lifeZone?.workstations?.[
        Math.floor(Math.random() * (lifeZone?.workstations?.length || 1))
      ] || [0, -12]
      const incomingConfig = agentData.characterConfig || {}
      const characterConfig = {
        name: agentData.name,
        personality_prompt: incomingConfig.personality_prompt || "",
        backstory_prompt: incomingConfig.backstory_prompt || "",
        quirks_and_mannerisms: incomingConfig.quirks_and_mannerisms || "",
        beliefs_and_intents: incomingConfig.beliefs_and_intents || "",
        verbosity: incomingConfig.verbosity || "medium",
        response_max_characters: responseMaxCharactersForVerbosity(
          incomingConfig.verbosity || "medium"
        ),
      }
      const chatAppCharacterProfile =
        buildChatAppCharacterProfile(characterConfig)
      const derivedStats = deriveStats(characterConfig, id)

      const newAgent = {
        id,
        name: agentData.name,
        role: agentData.role || "Agent",
        color: agentData.color || "#53b1fd",
        homeDistrict: agentData.homeDistrict || "residential_north",
        homeCity: agentData.homeCity || "",
        homeCoords: agentData.homeCoords || null,
        homeBbox: agentData.homeBbox || null,
        personalPlaces: agentData.personalPlaces || null,
        skills: agentData.skills || [],
        equippedSkills: agentData.equippedSkills || [
          "web_search",
          "write_note",
        ],
        stats: derivedStats,
        personality: {
          trait: deriveTrait(characterConfig),
          tags: deriveTags(characterConfig),
        },
        lifeSim: { baseActivities: ["eating", "sleep", "reading", "social"] },
        schedule: {
          wakeTime: agentData.wakeTime || 7,
          weekdaySlots: null,
          weekendSlots: null,
        },
        preferences: {
          socialEnergy: derivedStats.speed / 100,
          workEthic: derivedStats.reliability / 100,
          exercise: derivedStats.speed / 100,
          creativity: derivedStats.creativity / 100,
        },
        autonomyLevel: agentData.autonomyLevel ?? 0,
        directives: agentData.directives || {
          mission: "",
          behavioral: "",
          constraints: "",
        },
        characterConfig,
        chatAppCharacterId: agentData.chatAppCharacterId || null,
        chatAppCharacterProfile,
        profileDoc: {
          chat_app_model: agentData.chatAppModel || "lobster",
          character_config: characterConfig,
          chat_app_character_id: agentData.chatAppCharacterId || null,
          chat_app_character_profile: chatAppCharacterProfile,
        },
        // Runtime fields
        position: [startPos[0], 0, startPos[1]],
        targetPosition: [startPos[0], 0, startPos[1]],
        state: "idle",
        currentTask: null,
        currentZoneId: "life",
        mode: "life",
        activity: null,
        locationTag: null,
      }

      const now = new Date()
      const currentMin = now.getHours() * 60 + now.getMinutes()

      return {
        agents: [...s.agents, newAgent],
        lifeSimState: {
          ...s.lifeSimState,
          [id]: {
            needs: initNeeds(),
            firedToday: new Set(),
            simMin: currentMin,
            dayNum: 0,
            lastActId: null,
            actEndMin: 0,
          },
        },
      }
    }),

  removeAgent: (agentId) =>
    set((s) => {
      const nextLifeSim = { ...s.lifeSimState }
      delete nextLifeSim[agentId]
      const nextTasks = { ...s.activeTasks }
      delete nextTasks[agentId]
      const nextToolUse = { ...s.activeToolUse }
      delete nextToolUse[agentId]
      const floatingWindowOrder = removeFloatingWindowOrder(
        removeFloatingWindowOrder(
          removeFloatingWindowOrder(
            s.floatingWindowOrder || [],
            getCallWindowId(agentId)
          ),
          getChatEmbedWindowId(agentId)
        ),
        getTextChatWindowId(agentId)
      )

      return {
        agents: s.agents.filter((a) => a.id !== agentId),
        lifeSimState: nextLifeSim,
        activeTasks: nextTasks,
        activeToolUse: nextToolUse,
        activeCalls: (s.activeCalls || []).filter(
          (call) => call.agentId !== agentId
        ),
        activeChatEmbeds: (s.activeChatEmbeds || []).filter(
          (chatEmbed) => chatEmbed.agentId !== agentId
        ),
        openChats: s.openChats.filter((c) => c.agentId !== agentId),
        floatingWindowOrder,
        selectedAgentId:
          s.selectedAgentId === agentId ? null : s.selectedAgentId,
      }
    }),

  setAutonomyLevel: (agentId, level) =>
    set((s) => ({
      agents: s.agents.map((a) =>
        a.id === agentId
          ? { ...a, autonomyLevel: Math.max(0, Math.min(3, level)) }
          : a
      ),
    })),

  setDirectives: (agentId, directives) =>
    set((s) => ({
      agents: s.agents.map((a) =>
        a.id === agentId
          ? { ...a, directives: { ...a.directives, ...directives } }
          : a
      ),
    })),

  autonomousThinkState: {},
  setThinkState: (agentId, state) =>
    set((s) => ({
      autonomousThinkState: {
        ...s.autonomousThinkState,
        [agentId]: { ...s.autonomousThinkState[agentId], ...state },
      },
    })),

  // Call state
  activeCalls: [],

  startCall: (agentId) =>
    set((s) => {
      return {
        activeCalls: upsertAgentWindow(
          s.activeCalls || [],
          agentId,
          createCallWindow,
          (call) => ({
            ...restoreCallMinimizedMedia(call),
            minimized: false,
          })
        ),
        floatingWindowOrder: upsertFloatingWindowOrder(
          s.floatingWindowOrder || [],
          getCallWindowId(agentId)
        ),
      }
    }),

  endCall: (agentId) =>
    set((s) => ({
      activeCalls: (s.activeCalls || []).filter(
        (call) => call.agentId !== agentId
      ),
      floatingWindowOrder: removeFloatingWindowOrder(
        s.floatingWindowOrder || [],
        getCallWindowId(agentId)
      ),
    })),

  addParticipant: (agentId) =>
    set((s) => ({
      activeCalls: upsertAgentWindow(
        s.activeCalls || [],
        agentId,
        createCallWindow,
        (call) => ({
          ...restoreCallMinimizedMedia(call),
          minimized: false,
        })
      ),
      floatingWindowOrder: upsertFloatingWindowOrder(
        s.floatingWindowOrder || [],
        getCallWindowId(agentId)
      ),
    })),

  removeParticipant: (agentId) =>
    set((s) => ({
      activeCalls: (s.activeCalls || []).filter(
        (call) => call.agentId !== agentId
      ),
      floatingWindowOrder: removeFloatingWindowOrder(
        s.floatingWindowOrder || [],
        getCallWindowId(agentId)
      ),
    })),

  toggleCallMinimized: (agentId) =>
    set((s) => ({
      activeCalls: updateAgentWindow(s.activeCalls || [], agentId, (call) => {
        if (call.minimized) {
          return {
            ...restoreCallMinimizedMedia(call),
            minimized: false,
          }
        }

        return {
          ...call,
          minimized: true,
          preMinimizeUserAudio: call.userAudio,
          preMinimizeUserMic: call.userMic,
          userAudio: false,
          userMic: false,
        }
      }),
    })),

  toggleCallFullscreen: (agentId) =>
    set((s) => ({
      activeCalls: updateAgentWindow(s.activeCalls || [], agentId, (call) => ({
        ...call,
        fullscreen: !call.fullscreen,
      })),
    })),

  setCallPosition: (agentId, x, y) =>
    set((s) => ({
      activeCalls: updateAgentWindow(s.activeCalls || [], agentId, (call) => ({
        ...call,
        position: { x, y },
      })),
    })),

  setCallSize: (agentId, w, h) =>
    set((s) => ({
      activeCalls: updateAgentWindow(s.activeCalls || [], agentId, (call) => ({
        ...call,
        size: { w, h },
      })),
    })),

  toggleParticipantMute: (agentId) =>
    set((s) => ({
      activeCalls: updateAgentWindow(s.activeCalls || [], agentId, (call) => {
        const muted = call.mutedParticipants || []
        return {
          ...call,
          mutedParticipants: muted.includes(agentId)
            ? muted.filter((id) => id !== agentId)
            : [...muted, agentId],
        }
      }),
    })),

  toggleParticipantHidden: (agentId) =>
    set((s) => ({
      activeCalls: updateAgentWindow(s.activeCalls || [], agentId, (call) => {
        const hidden = call.hiddenFeeds || []
        return {
          ...call,
          hiddenFeeds: hidden.includes(agentId)
            ? hidden.filter((id) => id !== agentId)
            : [...hidden, agentId],
        }
      }),
    })),

  toggleUserCamera: (agentId) =>
    set((s) => ({
      activeCalls: updateAgentWindow(s.activeCalls || [], agentId, (call) => ({
        ...call,
        userCamera: !call.userCamera,
      })),
    })),

  toggleUserMic: (agentId) =>
    set((s) => ({
      activeCalls: updateAgentWindow(s.activeCalls || [], agentId, (call) => ({
        ...call,
        userMic: !call.userMic,
      })),
    })),

  toggleUserAudio: (agentId) =>
    set((s) => ({
      activeCalls: updateAgentWindow(s.activeCalls || [], agentId, (call) => ({
        ...call,
        userAudio: !call.userAudio,
      })),
    })),

  toggleUserScreen: (agentId) =>
    set((s) => ({
      activeCalls: updateAgentWindow(s.activeCalls || [], agentId, (call) => ({
        ...call,
        userScreen: !call.userScreen,
      })),
    })),
}))

export default useWorldStore
